import React, { useCallback, useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'X-Session-Token': token } : {};
};

const iosVersion = () => {
  const m = navigator.userAgent.match(/OS (\d+)_(\d+)(?:_(\d+))? like Mac OS X/);
  return m ? `${m[1]}.${m[2]}${m[3] ? '.' + m[3] : ''}` : 'n/a';
};

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-3 text-xs">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span data-testid={testId} className="font-medium text-right text-gray-800 dark:text-gray-200 break-words">{value}</span>
  </div>
);

const DeviceRow = ({ d, isThis }) => (
  <div data-testid={`push-device-${d.id}`} className={`p-2 rounded-lg text-xs border ${isThis ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-600'}`}>
    <p className="font-medium text-gray-800 dark:text-gray-200">
      {isThis ? 'This phone' : /iPhone|iPad/.test(d.user_agent) ? 'iPhone/iPad' : d.user_agent ? 'Other device' : 'Unknown device'} · …{d.endpoint_tail}
    </p>
    <p className="text-gray-500 dark:text-gray-400">
      Last send: {d.last_sent_at ? `${d.last_status === 201 ? 'accepted by Apple/Google' : `rejected (${d.last_status || 'error'})`} at ${new Date(d.last_sent_at + 'Z').toLocaleTimeString()}` : 'never'}
    </p>
    {d.last_error && <p className="text-red-500 break-all">{d.last_error}</p>}
  </div>
);

export const PushCheckPanel = () => {
  const [info, setInfo] = useState({});
  const [devices, setDevices] = useState([]);
  const [message, setMessage] = useState('');
  const [rejection, setRejection] = useState(null);
  const [sending, setSending] = useState(false);

  const refresh = useCallback(async () => {
    const reg = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : null;
    const sub = reg ? await reg.pushManager.getSubscription().catch(() => null) : null;
    setInfo({
      sw: reg?.active ? `active (${reg.active.scriptURL.split('/').pop()})` : 'not installed',
      endpointTail: sub ? sub.endpoint.slice(-12) : '',
      permission: 'Notification' in window ? Notification.permission : 'unsupported',
    });
    try {
      const res = await fetch(`${API_URL}/api/push/devices`, { credentials: 'include', headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDevices(data.devices || []);
        setRejection(data.last_rejection || null);
      }
    } catch (e) { /* offline */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const localTest = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification('Local test 🔔', { body: 'This came from your phone, not the server.', tag: 'local-test' });
      setMessage('Local notification shown. If you did not see it, check iPhone Settings > Notifications > RenziFitness.');
    } catch (e) {
      setMessage('Local notification failed: ' + e.message);
    }
  };

  const delayedTest = async () => {
    setSending(true);
    setTimeout(() => setSending(false), 12000);
    const res = await fetch(`${API_URL}/api/push/test?delay=10`, { method: 'POST', credentials: 'include', headers: authHeaders() });
    const data = await res.json().catch(() => ({}));
    setMessage(data.scheduled ? 'Sending in 10 seconds — lock your phone or close the app now. Then come back and tap Refresh.' : (data.message || 'Could not schedule test'));
  };

  const thisRegistered = info.endpointTail && devices.some(d => d.endpoint_tail === info.endpointTail);
  const btn = 'flex-1 p-2 text-xs font-semibold rounded-lg';

  return (
    <div data-testid="push-check-panel" className="p-3 rounded-lg border border-gray-200 dark:border-gray-600 space-y-2">
      <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">Notification check</p>
      <Row label="iOS version" value={iosVersion()} testId="push-check-ios" />
      <Row label="Permission" value={info.permission} testId="push-check-permission" />
      <Row label="Notification service" value={info.sw} testId="push-check-sw" />
      <Row label="This phone registered" value={info.endpointTail ? (thisRegistered ? `yes (…${info.endpointTail})` : `NO — turn notifications off and on`) : 'no subscription on this phone'} testId="push-check-registered" />
      <div className="space-y-1">{devices.map(d => <DeviceRow key={d.id} d={d} isThis={d.endpoint_tail === info.endpointTail} />)}</div>
      {rejection && (
        <p data-testid="push-check-last-rejection" className="text-xs text-red-500 break-words">
          Last rejected device (…{rejection.endpoint_tail}): {rejection.status} {rejection.reason} at {new Date(rejection.at + 'Z').toLocaleString()}
        </p>
      )}
      <div className="flex gap-2">
        <button data-testid="push-check-local-btn" onClick={localTest} className={`${btn} bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200`}>Test on phone only</button>
        <button data-testid="push-check-delayed-btn" onClick={delayedTest} disabled={sending} className={`${btn} bg-blue-600 text-white disabled:opacity-50`}>{sending ? 'Sending...' : 'Send in 10 seconds'}</button>
        <button data-testid="push-check-refresh-btn" onClick={() => { setMessage(''); refresh(); }} className={`${btn} bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200`}>Refresh</button>
      </div>
      {message && <p data-testid="push-check-message" className="text-xs text-blue-600 dark:text-blue-400">{message}</p>}
    </div>
  );
};
